import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import i18n from 'i18n';
import EVENT_NAMES from 'shared/constants/events';
import Emitter from 'shared/events/emitter';

import { createTicket, fetchCreditAvailable, fetchTicketsForEvent, fetchTicketsRich, requestRebuy } from './api';

function getBetErrorMessage(error: any): string {
  const data = error?.response?.data;
  if (data?.error && typeof data.error === 'string') return data.error;
  if (data?.message && typeof data.message === 'string') return data.message;
  if (error?.message && typeof error.message === 'string') return error.message;
  return i18n.t('BET_FAILED_MESSAGE') || 'Bet failed';
}

export function useCreateTicketMutation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTicket,
    onSuccess() {
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
    },
    onError(error: any) {
      const message = getBetErrorMessage(error);
      const title = i18n.t('BET_FAILED_TITLE') || 'Bet failed';
      (Emitter as { emit: (event: string, payload?: unknown) => void }).emit(EVENT_NAMES.DIALOG.SHOW, { title, message });
    },
  });
}

export const ticketsQueryKeys = {
  creditAvailable: ['tickets', 'credit', 'available'] as const,
  richList: ['tickets', 'rich'] as const,
  eventList: (eventId: number | string) => ['tickets', 'event', String(eventId)] as const,
};

export function useCreditAvailableQuery(params?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ticketsQueryKeys.creditAvailable,
    queryFn: fetchCreditAvailable,
    enabled: params?.enabled ?? true,
  });
}

export function useTicketsRichQuery(params?: { enabled?: boolean }) {
  return useQuery({
    queryKey: ticketsQueryKeys.richList,
    queryFn: fetchTicketsRich,
    enabled: params?.enabled ?? true,
    refetchInterval: 30_000,
  });
}

export function useTicketsForEventQuery(
  eventId: number | string | null | undefined,
  params?: { enabled?: boolean }
) {
  return useQuery({
    queryKey: ticketsQueryKeys.eventList(eventId ?? ''),
    queryFn: () => fetchTicketsForEvent(eventId as number | string),
    enabled: (params?.enabled ?? true) && eventId != null && String(eventId) !== '',
  });
}

export function useRebuyMutation() {
  return useMutation({
    mutationFn: requestRebuy,
  });
}

